import React, { useState, useEffect, useCallback, useRef } from 'react';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import { utils } from '../utils';
import { RootState } from '../store';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { showDownloadProgress, hideDownloadProgress } from '../utils/downloadFile';
import { ClipLoader } from 'react-spinners';
import {
  MdDownload, MdVisibility, MdReceiptLong,
  MdCalendarToday, MdClose, MdSearchOff, MdErrorOutline, MdOpenInNew,
} from 'react-icons/md';

const OpenNewIcon  = MdOpenInNew as any;
const DownloadIcon = MdDownload as any;
const ViewIcon     = MdVisibility as any;
const ReceiptIcon  = MdReceiptLong as any;
const CalendarIcon = MdCalendarToday as any;
const CloseIcon    = MdClose as any;
const EmptyIcon    = MdSearchOff as any;
const ErrorIcon    = MdErrorOutline as any;

const isMobileDevice = () => /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/$/, '');

interface Slip {
  id: number;
  payroll_period: string;
  cutoff_start: string | null;
  cutoff_end: string | null;
  created_at: string;
}

// Purple shades per month untuk variasi visual
const MONTH_SHADES = [
  '#6D28D9','#7C3AED','#8B5CF6','#7C3AED','#6D28D9','#5B21B6',
  '#7C3AED','#8B5CF6','#9333EA','#7C3AED','#6D28D9','#8B5CF6',
];

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const parsePeriod = (dateStr: string) => {
  const d = new Date(dateStr);
  return { monthIdx: d.getUTCMonth(), year: d.getUTCFullYear() };
};

export const Payslip: React.FC = () => {
  const auth     = hooks.useAppSelector((state: RootState) => state.authSlice);
  const { t }    = useLanguage();
  const tp       = (t as any).payslip || {};
  const userName = auth.userName || t.personalData.employeeLabel;

  const [slips, setSlips]                 = useState<Slip[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [selectedYear, setSelectedYear]   = useState<number>(new Date().getFullYear());
  const [showAll, setShowAll]             = useState(false);
  const [pdfModal, setPdfModal] = useState<{ slipId: number; url: string; title: string; downloadUrl: string; filename: string } | null>(null);
  const [pdfPages, setPdfPages]               = useState<string[]>([]);
  const [pdfPagesLoading, setPdfPagesLoading] = useState(false);
  const [pdfPagesError, setPdfPagesError]     = useState<string | null>(null);
  const [mobileZoom, setMobileZoom]           = useState(1);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [passwordIncorrect, setPasswordIncorrect]   = useState(false);
  const [pdfPasswordInput, setPdfPasswordInput]     = useState('');
  const pendingPasswordCb  = useRef<((p: string) => void) | null>(null);
  const pdfLoadingTaskRef  = useRef<any>(null);

  const fetchSlips = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.get('/payroll/my-slips');
      setSlips(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || tp.fetchError || 'Gagal memuat data slip gaji.');
    } finally {
      setLoading(false);
    }
  }, [tp.fetchError]);

  useEffect(() => { fetchSlips(); }, [fetchSlips]);

  const availableYears  = Array.from(new Set(slips.map(s => new Date(s.payroll_period).getUTCFullYear()))).sort((a, b) => b - a);
  const slipsForYear    = slips.filter(s => new Date(s.payroll_period).getUTCFullYear() === selectedYear);
  const last3Periods    = slips.slice(0, 3).map(s => s.payroll_period);
  const visibleSlips    = showAll ? slipsForYear : slipsForYear.filter(s => last3Periods.includes(s.payroll_period));
  const hiddenCount     = slipsForYear.length - visibleSlips.length;

  const buildSlipUrls = (slip: Slip) => {
    const token = encodeURIComponent(auth.accessToken || '');
    const viewUrl     = `${API_BASE}/payroll/slips/${slip.id}/view?token=${token}`;
    const downloadUrl = `${API_BASE}/payroll/slips/${slip.id}/download?token=${token}`;
    return { viewUrl, downloadUrl };
  };

  const handleView = (slip: Slip) => {
    const { viewUrl, downloadUrl } = buildSlipUrls(slip);
    const { monthIdx, year } = parsePeriod(slip.payroll_period);
    const month = t.leaveHistory.months[monthIdx];
    setPdfModal({
      slipId: slip.id,
      url: viewUrl,
      downloadUrl,
      title: `${(tp as any).modalTitlePrefix || 'Slip Gaji –'} ${month} ${year}`,
      filename: `SlipGaji_${month}_${year}.pdf`,
    });
  };

  const shareOrDownloadSlip = useCallback(async (slipId: number, filename: string, downloadUrl: string) => {
    // Fetch via axiosInstance — butuh auth token untuk endpoint ini
    try {
      showDownloadProgress(0, 'Mengunduh slip gaji...');
      const res = await axiosInstance.get(`/payroll/slips/${slipId}/download`, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded / progressEvent.total) * 90);
            showDownloadProgress(pct, 'Mengunduh slip gaji...');
          }
        },
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });

      if (Capacitor.isNativePlatform()) {
        showDownloadProgress(92, 'Menyimpan PDF...');
        // Simpan ke Documents app. window.open(file://) diblokir Android 7+,
        // jadi cukup beri tahu user file sudah tersimpan.
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Documents, recursive: true });
        showDownloadProgress(100, 'PDF tersimpan ✓');
        hideDownloadProgress('PDF tersimpan ✓');
        return;
      }

      // Web browser: blob + anchor
      showDownloadProgress(100, 'Mengunduh slip gaji...');
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      hideDownloadProgress('Download selesai ✓');
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      }, 10000);
    } catch {
      hideDownloadProgress('Download gagal');
      utils.downloadFile(downloadUrl, filename);
    }
  }, []);

  const handleDownload = (slip: Slip) => {
    const { downloadUrl } = buildSlipUrls(slip);
    const { monthIdx, year } = parsePeriod(slip.payroll_period);
    const month = t.leaveHistory.months[monthIdx];
    shareOrDownloadSlip(slip.id, `SlipGaji_${month}_${year}.pdf`, downloadUrl);
  };

  const closePdf = () => {
    if (pdfLoadingTaskRef.current) {
      pdfLoadingTaskRef.current.destroy();
      pdfLoadingTaskRef.current = null;
    }
    pendingPasswordCb.current = null;
    setShowPasswordPrompt(false);
    setPdfPasswordInput('');
    setPdfModal(null);
  };

  // ── PDF.js mobile renderer ───────────────────────────────────────────────────

  const loadPdfForMobile = useCallback(async (slipId: number) => {
    setPdfPages([]);
    setPdfPagesLoading(true);
    setPdfPagesError(null);
    setShowPasswordPrompt(false);
    setPasswordIncorrect(false);
    setPdfPasswordInput('');

    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

      const response = await axiosInstance.get(`/payroll/slips/${slipId}/view`, {
        responseType: 'arraybuffer',
      });

      const loadingTask = pdfjsLib.getDocument({ data: response.data });
      pdfLoadingTaskRef.current = loadingTask;

      // Handle password-protected PDFs
      (loadingTask as any).onPassword = (updatePassword: (p: string) => void, reason: number) => {
        pendingPasswordCb.current = updatePassword;
        setPasswordIncorrect(reason === 2); // reason 2 = wrong password
        setShowPasswordPrompt(true);
        setPdfPagesLoading(false);
      };

      const pdf = await loadingTask.promise;
      pdfLoadingTaskRef.current = null;

      const pages: string[] = [];
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: dpr });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        await page.render({ canvasContext: ctx, viewport }).promise;
        pages.push(canvas.toDataURL('image/jpeg', 0.88));
      }

      setPdfPages(pages);
    } catch (err: any) {
      // PasswordException saat user cancel dari password prompt
      if (err?.name === 'PasswordException') return;
      setPdfPagesError((tp as any).pdfLoadError || 'Gagal memuat PDF. Gunakan tombol unduh untuk membukanya.');
    } finally {
      setPdfPagesLoading(false);
    }
  }, [tp]);

  useEffect(() => {
    if (!pdfModal) {
      setPdfPages([]);
      setPdfPagesError(null);
      return;
    }
    setMobileZoom(1);
    if (isMobileDevice()) {
      loadPdfForMobile(pdfModal.slipId);
    }
  }, [pdfModal, loadPdfForMobile]);

  const handlePasswordSubmit = () => {
    if (!pendingPasswordCb.current || !pdfPasswordInput.trim()) return;
    const cb = pendingPasswordCb.current;
    pendingPasswordCb.current = null;
    setShowPasswordPrompt(false);
    setPdfPagesLoading(true);
    cb(pdfPasswordInput.trim());
    setPdfPasswordInput('');
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const renderWelcomeBanner = () => (
    <div style={{
      margin: '16px 16px 0',
      borderRadius: 16,
      background: 'linear-gradient(135deg, #6D28D9 0%, #8B5CF6 100%)',
      padding: '18px 20px',
      display: 'flex', alignItems: 'center', gap: 14,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', right: -16, top: -16, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
      <div style={{ position: 'absolute', right: 20, bottom: -20, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
      <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <ReceiptIcon size={24} color="#fff" />
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>{tp.title || 'Slip Gaji'}</div>
        <div style={{ color: '#fff', fontSize: 15, fontWeight: 700, textTransform: 'capitalize' }}>{userName}</div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 1 }}>
          {slips.length > 0
            ? `${slips.length} ${tp.slipsAvailable || 'slip tersedia'}`
            : loading ? t.common.loading : tp.noSlipYet || 'Belum ada slip'}
        </div>
      </div>
    </div>
  );

  const renderYearTabs = () => {
    if (availableYears.length === 0) return null;
    return (
      <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', borderBottom: `1px solid ${theme.colors.aliceBlue}`, marginTop: 16 }}>
        <div style={{ display: 'inline-flex', padding: '0 16px', gap: 4 }}>
          {availableYears.map(year => (
            <button key={year} onClick={() => { setSelectedYear(year); setShowAll(year !== new Date().getFullYear()); }} style={{
              border: 'none', cursor: 'pointer', padding: '10px 18px', fontSize: 13, fontWeight: 600,
              borderBottom: selectedYear === year ? '2px solid #8B5CF6' : '2px solid transparent',
              color: selectedYear === year ? '#7C3AED' : theme.colors.bodyTextColor,
              background: 'none',
            }}>{year}</button>
          ))}
        </div>
      </div>
    );
  };

  const renderSlipCard = (slip: Slip) => {
    const { monthIdx, year } = parsePeriod(slip.payroll_period);
    const month      = t.leaveHistory.months[monthIdx];
    const monthShort = tp.monthsShort?.[monthIdx] || month.slice(0, 3).toUpperCase();
    const accent     = MONTH_SHADES[monthIdx];
    const isCurrentMonth = year === new Date().getFullYear() && monthIdx === new Date().getMonth();

    return (
      <div key={slip.id} style={{
        background: theme.colors.white,
        borderRadius: 16,
        marginBottom: 10,
        boxShadow: '0 1px 6px rgba(109,40,217,0.07)',
        overflow: 'hidden',
        border: isCurrentMonth ? `1.5px solid ${accent}` : `1.5px solid ${theme.colors.aliceBlue}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px 12px' }}>
          {/* Month badge */}
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: `${accent}18`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, marginRight: 14,
          }}>
            <span style={{ color: accent, fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>{monthShort}</span>
            <span style={{ color: accent, fontSize: 17, fontWeight: 900, lineHeight: 1.1 }}>'{String(year).slice(2)}</span>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <text.H5 style={{ color: theme.colors.mainDark, margin: 0 }}>{month} {year}</text.H5>
              {isCurrentMonth && (
                <span style={{ background: accent, color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 20 }}>{tp.thisMonth || 'BULAN INI'}</span>
              )}
            </div>

            {(slip.cutoff_start || slip.cutoff_end) ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <CalendarIcon size={11} color={theme.colors.bodyTextColor} />
                <text.T14 style={{ color: theme.colors.bodyTextColor, fontSize: 11, margin: 0 }}>
                  {formatDate(slip.cutoff_start)} – {formatDate(slip.cutoff_end)}
                </text.T14>
              </div>
            ) : (
              <text.T14 style={{ color: theme.colors.bodyTextColor, fontSize: 11, margin: 0 }}>
                {tp.period || 'Periode'} {month} {year}
              </text.T14>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', borderTop: `1px solid ${theme.colors.aliceBlue}` }}>
          <button onClick={() => handleView(slip)} style={{
            flex: 1, padding: '11px 0', border: 'none', background: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            color: '#7C3AED', fontSize: 13, fontWeight: 600,
            borderRight: `1px solid ${theme.colors.aliceBlue}`,
          }}>
            <ViewIcon size={15} />
            {tp.view || 'Lihat'}
          </button>
          <button onClick={() => handleDownload(slip)} style={{
            flex: 1, padding: '11px 0', border: 'none', background: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            color: '#059669', fontSize: 13, fontWeight: 600,
          }}>
            <DownloadIcon size={15} />
            {tp.download || 'Unduh'}
          </button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return (
      <div style={{ padding: '16px 16px 0' }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{ background: theme.colors.aliceBlue, borderRadius: 16, height: 96, marginBottom: 10 }} />
        ))}
      </div>
    );

    if (error) return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', textAlign: 'center' }}>
        <ErrorIcon size={48} color="#EF4444" />
        <text.H5 style={{ marginTop: 12 }}>{tp.loadFailed || 'Gagal Memuat'}</text.H5>
        <text.T14 style={{ color: theme.colors.bodyTextColor, marginBottom: 20 }}>{error}</text.T14>
        <components.Button title={tp.retry || 'Coba Lagi'} onClick={fetchSlips} containerStyle={{ width: 160 }} />
      </div>
    );

    if (slips.length === 0) return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 20px', textAlign: 'center' }}>
        <EmptyIcon size={52} color={theme.colors.aliceBlue} />
        <text.H5 style={{ marginTop: 12, color: theme.colors.mainDark }}>{tp.noSlipTitle || 'Belum Ada Slip'}</text.H5>
        <text.T14 style={{ color: theme.colors.bodyTextColor }}>{tp.noSlipDesc || 'Slip gaji Anda akan muncul di sini setelah diproses oleh HR.'}</text.T14>
      </div>
    );

    return (
      <div style={{ padding: '14px 16px 80px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <text.T14 style={{ color: theme.colors.mainDark, fontWeight: 600, margin: 0 }}>
            {showAll ? `${tp.allOf || 'Semua –'} ${selectedYear}` : tp.last3Months || '3 Bulan Terakhir'}
            <span style={{ color: theme.colors.bodyTextColor, fontWeight: 400, marginLeft: 6 }}>({visibleSlips.length})</span>
          </text.T14>
          {!showAll && slipsForYear.length > 0 && selectedYear === new Date().getFullYear() && (
            <button onClick={() => setShowAll(true)} style={{ background: 'none', border: 'none', color: '#7C3AED', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
              {tp.viewAll || 'Lihat semua →'}
            </button>
          )}
        </div>

        {visibleSlips.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: theme.colors.bodyTextColor, fontSize: 13 }}>
            {tp.noSlipForYear || 'Tidak ada slip untuk tahun'} {selectedYear}
          </div>
        ) : (
          visibleSlips.map(renderSlipCard)
        )}

        {!showAll && hiddenCount > 0 && (
          <button onClick={() => setShowAll(true)} style={{
            width: '100%', padding: 14, borderRadius: 16,
            border: '1.5px dashed #C4B5FD', background: '#FAFAFA',
            color: '#7C3AED', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            + {hiddenCount} {tp.moreSlips || 'slip lainnya di'} {selectedYear}
          </button>
        )}
      </div>
    );
  };

  const renderPdfModal = () => {
    if (!pdfModal) return null;
    const isMobile = isMobileDevice();

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: '#111827', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', background: '#1F2937', flexShrink: 0 }}>
          <text.T14 style={{ color: '#fff', fontWeight: 600, margin: 0, flex: 1 }}>{pdfModal.title}</text.T14>
          <button
            onClick={() => shareOrDownloadSlip(pdfModal.slipId, pdfModal.filename, pdfModal.downloadUrl)}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
            title="Download"
          >
            <DownloadIcon size={16} color="#fff" />
          </button>
          <button onClick={closePdf} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            <CloseIcon size={18} color="#fff" />
          </button>
        </div>

        {isMobile ? (
          /* Mobile: PDF.js canvas rendering — works in Chrome and WebView */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' as any, background: '#1a1a2e' }}>
              <div style={{ width: `${mobileZoom * 100}%` }}>
                {pdfPagesLoading && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16 }}>
                    <ClipLoader size={36} color="#8B5CF6" />
                    <text.T14 style={{ color: 'rgba(255,255,255,0.55)', margin: 0 }}>
                      {(tp as any).loadingPdf || 'Memuat PDF...'}
                    </text.T14>
                  </div>
                )}
                {pdfPagesError && !pdfPagesLoading && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '52px 24px', gap: 16 }}>
                    <ErrorIcon size={48} color="#F87171" />
                    <text.T14 style={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', margin: 0 }}>{pdfPagesError}</text.T14>
                    <button
                      onClick={() => loadPdfForMobile(pdfModal.slipId)}
                      style={{ background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {(tp as any).retry || 'Coba Lagi'}
                    </button>
                    <button
                      onClick={() => shareOrDownloadSlip(pdfModal.slipId, pdfModal.filename, pdfModal.downloadUrl)}
                      style={{ background: 'none', color: 'rgba(255,255,255,0.45)', border: 'none', fontSize: 13, cursor: 'pointer', padding: 0 }}
                    >
                      {(tp as any).downloadInstead || 'Download PDF'}
                    </button>
                  </div>
                )}
                {!pdfPagesLoading && pdfPages.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Halaman ${i + 1}`}
                    style={{ width: '100%', display: 'block', marginBottom: i < pdfPages.length - 1 ? 4 : 0 }}
                  />
                ))}
              </div>
            </div>
            {pdfPages.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', background: '#111827', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 20, padding: '5px 14px' }}>
                  <button onClick={() => setMobileZoom(z => Math.max(0.5, z - 0.25))} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 30 }}>−</button>
                  <span style={{ color: 'white', fontSize: 13, minWidth: 40, textAlign: 'center' }}>{Math.round(mobileZoom * 100)}%</span>
                  <button onClick={() => setMobileZoom(z => Math.min(3, z + 0.25))} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 30 }}>+</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Desktop: iframe bisa render PDF langsung */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <div style={{ width: `${mobileZoom * 100}%`, height: `${mobileZoom * 100}%`, minHeight: '100%' }}>
                <iframe src={pdfModal.url} style={{ width: '100%', height: '100%', border: 'none' }} title={pdfModal.title} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', background: '#111827', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 20, padding: '5px 14px' }}>
                <button onClick={() => setMobileZoom(z => Math.max(0.5, z - 0.25))} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 30 }}>−</button>
                <span style={{ color: 'white', fontSize: 13, minWidth: 40, textAlign: 'center' }}>{Math.round(mobileZoom * 100)}%</span>
                <button onClick={() => setMobileZoom(z => Math.min(3, z + 0.25))} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 30 }}>+</button>
              </div>
            </div>
          </div>
        )}

        {/* Password prompt overlay (muncul di atas konten saat PDF berpassword) */}
        {showPasswordPrompt && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}>
            <div style={{
              background: '#1F2937', borderRadius: 20, padding: 24,
              width: '100%', maxWidth: 360,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}>
              <text.H5 style={{ color: '#fff', margin: '0 0 6px' }}>
                {(tp as any).pdfPasswordTitle || 'PDF Dilindungi Password'}
              </text.H5>
              <text.T14 style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 20px' }}>
                {passwordIncorrect
                  ? ((tp as any).pdfPasswordWrong || 'Password salah. Coba lagi.')
                  : ((tp as any).pdfPasswordPrompt || 'Masukkan password untuk membuka PDF ini.')}
              </text.T14>
              <input
                type="password"
                autoFocus
                value={pdfPasswordInput}
                onChange={e => setPdfPasswordInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handlePasswordSubmit(); }}
                placeholder={(tp as any).pdfPasswordPlaceholder || 'Password PDF...'}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: 'rgba(255,255,255,0.08)',
                  border: passwordIncorrect ? '1.5px solid #F87171' : '1.5px solid rgba(255,255,255,0.15)',
                  borderRadius: 12, padding: '13px 16px',
                  color: '#fff', fontSize: 15, outline: 'none',
                  marginBottom: 16,
                }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={closePdf}
                  style={{
                    flex: 1, padding: '13px 0', borderRadius: 12,
                    background: 'rgba(255,255,255,0.08)', border: 'none',
                    color: 'rgba(255,255,255,0.6)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {(tp as any).cancel || t.common.cancel}
                </button>
                <button
                  onClick={handlePasswordSubmit}
                  disabled={!pdfPasswordInput.trim()}
                  style={{
                    flex: 2, padding: '13px 0', borderRadius: 12,
                    background: pdfPasswordInput.trim() ? '#7C3AED' : 'rgba(124,58,237,0.4)',
                    border: 'none', color: '#fff', fontSize: 14, fontWeight: 700,
                    cursor: pdfPasswordInput.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  {(tp as any).pdfPasswordOpen || 'Buka PDF'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div id='screen' style={{ backgroundColor: theme.colors.white, minHeight: '100vh' }}>
      <components.Header title={tp.title || 'Slip Gaji'} goBack={true} />
      <main style={{ marginTop: 52, paddingBottom: 80 }}>
        {renderWelcomeBanner()}
        {renderYearTabs()}
        {renderContent()}
      </main>
      {renderPdfModal()}
    </div>
  );
};
